import React, { Component } from 'react';
import { Button, Form } from 'reactstrap';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import { createForm } from '../../../models/Form';
import globalTranslations from '../../../i18n/globalTranslations';
import FormGroupField from '../../form/FormGroupField';
import { isDate, notBlank } from '../../../util/formValidators';
import { modelOf } from '../../../prop-types';
import { createFormField } from '../../../models/FormField';
import CheckboxField from '../../form/CheckboxField';
import SelectField from '../../form/SelectField';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import CurrencyStore from '../../../store/CurrencyStore';
import RecurringOrder from '../../../models/RecurringOrder';
import Address from '../../../models/Address';
import ShippingOption from '../../../models/ShippingOption';
import { formatPrice } from '../../../util/number';
import { labels } from '../../../util/formLabels';
import OrderStatusClass from '../../../types/OrderStatusClass';

function createOption(value, label) {
  return {
    value,
    label,
  };
}

@observer
export class RecurringOrderForm extends Component {
  form = null;
  formName = 'RecurringOrderForm';

  constructor(props) {
    super(props);
    const { recurringOrder } = this.props;

    this.form = createForm({
      title: createFormField({ value: recurringOrder && recurringOrder.title }),
      status: createFormField({
        value:
          recurringOrder &&
          recurringOrder.recurring_status === OrderStatusClass.ACTIVE
            ? '1'
            : '',
      }),
      allowUnderDelivery: createFormField({
        value: recurringOrder && recurringOrder.allow_under_delivery ? '1' : '',
      }),
      allowShippingMethodChange: createFormField({
        value:
          recurringOrder && recurringOrder.allow_shipping_method_change
            ? '1'
            : '',
      }),
      onHoldUntil: createFormField(
        {
          value:
            recurringOrder.on_hold_until &&
            DateTime.fromISO(recurringOrder.on_hold_until).toISODate(),
        },
        [isDate(labels.onHoldUntil, true)]
      ),
      deliveryAddressId: createFormField(
        { value: recurringOrder && recurringOrder.delivery_address_id },
        [notBlank(labels.deliveryAddressId)]
      ),
      shippingMethodId: createFormField(
        { value: recurringOrder && recurringOrder.shipping.method.id },
        []
      ),
    });
  }

  getCheckboxValue = (value) => (value.length > 0 ? '1' : '0');

  getStatusValue = (value) => {
    return value > 0 ? OrderStatusClass.ACTIVE : OrderStatusClass.DEACTIVATED;
  };

  getDateValue = (value) => {
    if (value === '') {
      return null;
    }
    return value;
  };

  handleSubmit = (event) => {
    const { onSubmit } = this.props;
    event.preventDefault();
    this.form.validate();
    if (this.form.valid) {
      const values = this.form.getFieldValues();

      values.allowUnderDelivery = this.getCheckboxValue(
        values.allowUnderDelivery
      );
      values.allowShippingMethodChange = this.getCheckboxValue(
        values.allowShippingMethodChange
      );
      values.status = this.getStatusValue(values.status);
      values.deliveryAddressId = Number(values.deliveryAddressId);
      values.onHoldUntil = this.getDateValue(values.onHoldUntil);

      onSubmit(values);
    }
  };

  field = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <FormGroupField
          className="RecurringOrderForm__field"
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          {...props}
        />
      )
    );
  };

  checkbox = (fieldName, props) => {
    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <CheckboxField
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          {...props}
        />
      )
    );
  };

  date = () => {
    const { preventDate } = this.props;
    const fieldName = 'onHoldUntil';

    const field = this.form.fields.get(fieldName);
    return (
      field.active && (
        <div className="RecurringOrderForm__dateField">
          <FormGroupField
            field={field}
            type="date"
            required={false}
            formName={this.formName}
            fieldName={fieldName}
            label={labels[fieldName]}
            min={preventDate || DateTime.local().toISODate()}
            onKeyDown={(e) => e.preventDefault()}
          />
        </div>
      )
    );
  };

  getReadableAddress = (address) => {
    const readable = [];

    if (address.street_address) {
      readable.push(address.street_address);
    }
    if (address.postal_code) {
      readable.push(address.postal_code + ' ' + address.city);
    }
    if (address.country) {
      readable.push(address.country.name);
    }
    return readable.join(', ');
  };

  getReadableShippingOption = (option) => {
    const { currencyStore } = this.props;

    return (
      option.name +
      ' ' +
      formatPrice(option.price.getPrice(true), currencyStore.activeCurrency)
    );
  };

  renderAddressSelect = () => {
    const { addresses } = this.props;

    if (!addresses || addresses.length < 1) {
      return null;
    }

    const fieldName = 'deliveryAddressId';
    const field = this.form.fields.get(fieldName);

    return (
      field.active && (
        <SelectField
          className="RecurringOrderForm__selectField"
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          options={addresses.map((address) =>
            createOption(String(address.id), this.getReadableAddress(address))
          )}
        />
      )
    );
  };

  renderShippingSelect = () => {
    const { shippingOptions } = this.props;

    if (!shippingOptions) {
      return null;
    }

    const fieldName = 'shippingMethodId';
    const field = this.form.fields.get(fieldName);

    return (
      field.active && (
        <SelectField
          className="RecurringOrderForm__selectField"
          field={field}
          formName={this.formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          options={shippingOptions.map((option) =>
            createOption(
              String(option.id),
              this.getReadableShippingOption(option)
            )
          )}
        />
      )
    );
  };

  render() {
    const { loading } = this.props;

    return (
      <Form className={this.formName} noValidate onSubmit={this.handleSubmit}>
        <div className={this.formName + '__content'}>
          <div className={this.formName + '__title'}>
            {this.field('title', {
              autoComplete: 'title',
            })}
            {this.renderAddressSelect()}
            {this.renderShippingSelect()}
          </div>
          <div className={this.formName + '__settings'}>
            {this.date()}
            {this.checkbox('status', {
              options: [createOption('1', labels.status)],
            })}
            {this.checkbox('allowUnderDelivery', {
              options: [createOption('1', labels.allowUnderDelivery)],
            })}
            {this.checkbox('allowShippingMethodChange', {
              options: [createOption('1', labels.allowShippingMethodChange)],
            })}
          </div>
        </div>
        <Button color="primary" disabled={this.form.valid === false || loading}>
          <FormattedMessage {...globalTranslations.update} />
        </Button>
      </Form>
    );
  }
}

RecurringOrderForm.propTypes = {
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  recurringOrder: modelOf(RecurringOrder),
  addresses: PropTypes.oneOfType([
    MobxPropTypes.observableArrayOf(modelOf(Address)),
    PropTypes.arrayOf(modelOf(Address)),
  ]),
  shippingOptions: PropTypes.oneOfType([
    MobxPropTypes.observableArrayOf(modelOf(ShippingOption)),
    PropTypes.arrayOf(modelOf(ShippingOption)),
  ]),
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  preventDate: PropTypes.string,
};

export default inject(
  'currencyStore',
  'recurringOrderStore'
)(RecurringOrderForm);
